import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Cookies } from "react-cookie";
import { useTheme } from "@mui/material/styles";
import { Game } from "../../types/custom_types";
import Navbar from "./navbar";
import Footer from "./footer";
import Loading from "./Loading/Loading";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import {
  Bracket,
  IRoundProps,
  IRenderSeedProps,
  SingleLineSeed,
  SeedTeam,
  SeedItem,
  Seed,
} from "react-brackets";

const BracketMaker = () => {
  const navigate = useNavigate();

  const theme = useTheme();
  const [games, setGames] = useState<Game[]>([]);
  const [rounds, setRounds] = useState<IRoundProps[]>([]);
  const [firstFour, setFirstFour] = useState<IRoundProps[]>([]);
  const [loading, setLoading] = useState(true);
  const bracketNameRef = useRef<HTMLInputElement>(null);

  const roundTitles = [
    "First Four",
    "Round 1",
    "Round 2",
    "Sweet 16",
    "Elite 8",
    "Final 4",
    "Championship",
  ];

  // Check if the user is logged in
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/userapi/check-auth/`,
          { withCredentials: true }
        );
        if (response.status !== 200) navigate("/login");
      } catch (error) {
        navigate("/login");
      }
    };

    checkLoginStatus();

    axios.defaults.xsrfCookieName = "csrftoken";
    axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchGames = async () => {
      const response = await axios.get<Game[]>(
        `${process.env.REACT_APP_API_URL}/userapi/games/`
      );
      setGames(response.data);
    };

    fetchGames();
  }, []);

  useEffect(() => {
    if (games.length === 0) return;

    const firstFourGames = games.filter((game) => game.round === 0);
    const firstFourSeeds = firstFourGames.map((game) => ({
      id: game.id,
      teams: [
        {
          name: game.team1_name,
          isWinner: game.winner_name === game.team1_name,
          seed: game.team1_seed,
          wins: game.team1_wins,
          losses: game.team1_losses,
        },
        {
          name: game.team2_name,
          isWinner: game.winner_name === game.team2_name,
          seed: game.team2_seed,
          wins: game.team2_wins,
          losses: game.team2_losses,
        },
      ],
    }));
    setFirstFour([{ title: "First Four", seeds: firstFourSeeds }]);

    const roundMap = new Map<number, Game[]>();
    games.forEach((game) => {
      if (game.round === 0) return;
      if (!roundMap.has(game.round)) roundMap.set(game.round, []);
      roundMap.get(game.round)?.push(game);
    });

    const rounds = Array.from(roundMap.entries()).map(([round, games]) => ({
      title: roundTitles[round],
      seeds: games.map((game) => ({
        id: game.id,
        teams: [
          {
            name: game.team1_name,
            isWinner: game.winner_name && game.team1_name === game.winner_name,
            seed: game.team1_seed,
            wins: game.team1_wins,
            losses: game.team1_losses,
          },
          {
            name: game.team2_name,
            isWinner: game.winner_name && game.team2_name === game.winner_name,
            seed: game.team2_seed,
            wins: game.team2_wins,
            losses: game.team2_losses,
          },
        ],
      })),
    }));

    setRounds(rounds);
    setLoading(false);
  }, [games]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectWinner = (
    selectedGame: Game,
    winnerTeam: "team1_name" | "team2_name"
  ) => {
    let updatedGames = games.map((game) => ({ ...game }));

    updatedGames = updatedGames.map((game) => {
      if (game.id === selectedGame.id) {
        return { ...game, winner_name: selectedGame[winnerTeam] };
      }
      return game;
    });

    const otherTeam = winnerTeam === "team1_name" ? "team2_name" : "team1_name";
    const otherTeamName = selectedGame[otherTeam];

    const clearFutureWinners = (round: number, updatedGames: Game[]): void => {
      updatedGames.forEach((game) => {
        if (game.round <= round) return;
        if (game.team1_name === otherTeamName) game.team1_name = undefined;
        else if (game.team2_name === otherTeamName) game.team2_name = undefined;
        if (game.winner_name === otherTeamName) game.winner_name = undefined;
      });
    };

    if (selectedGame.winner_name !== selectedGame[winnerTeam]) {
      clearFutureWinners(selectedGame.round, updatedGames);
    }

    updatedGames.forEach((game) => {
      if (game.previous_game_for_team1_id === selectedGame.id) {
        game.team1_name = selectedGame[winnerTeam];
      } else if (game.previous_game_for_team2_id === selectedGame.id) {
        game.team2_name = selectedGame[winnerTeam];
      }
    });

    setGames(updatedGames);
  };

  const handlePartyClick = (game_id: number, party_won: "team1" | "team2") => {
    const game = games.find((game) => game.id === game_id);
    if (!game) return;
    handleSelectWinner(
      game,
      party_won === "team1" ? "team1_name" : "team2_name"
    );
  };

  const handleSubmit = async () => {
    const allGamesHaveWinner = games.every((game) => game.winner_name);
    if (!allGamesHaveWinner) {
      alert("Not all games have a winner yet.");
      return;
    }

    const bracketName = bracketNameRef.current?.value;
    if (!bracketName) {
      alert("Please enter a bracket name.");
      return;
    }

    const predictions = games.map((game) => ({
      game_id: game.id,
      predicted_team1: game.team1_name,
      predicted_team2: game.team2_name,
      predicted_winner: game.winner_name,
    }));

    try {
      console.log("sending predictions to the API");
      const csrfToken = new Cookies().get("csrftoken");
      console.log("csrfToken", csrfToken);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/userapi/predictions/`,
        { bracket_name: bracketName, predictions },
        { withCredentials: true, headers: { "X-CSRFTOKEN": csrfToken } }
      );
      if (response.status === 201) {
        console.log("Predictions submitted successfully");
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to submit predictions", error);
    }
  };

  function handleClick(
    event: React.MouseEvent,
    game_id: number,
    party_won: "team1" | "team2"
  ) {
    event.preventDefault();
    console.log("SeedTeam was clicked");
    handlePartyClick(game_id, party_won);
  }

  const CustomSeed = ({
    seed,
    breakpoint,
    roundIndex,
    isMiddleOfTwoSided,
  }: IRenderSeedProps) => {
    const Wrapper =
      (roundIndex === 4 || roundIndex === 0) && isMiddleOfTwoSided
        ? SingleLineSeed
        : Seed;
    return (
      <Wrapper mobileBreakpoint={breakpoint} style={{ fontSize: 12 }}>
        <SeedItem>
          <div>
            <SeedTeam
              onClick={(e: React.MouseEvent) =>
                handleClick(e, seed.id as number, "team1")
              }
              style={
                seed.teams[0]?.isWinner
                  ? {
                      fontWeight: "bold",
                      color: theme.palette.secondary.light,
                      borderLeft: `3px solid ${theme.palette.secondary.main}`,
                    }
                  : { borderLeft: "3px solid transparent" }
              }
            >
              {seed.teams[0].seed && `${seed.teams[0].seed} `}
              {seed.teams[0]?.name || " - "}
              {seed.teams[0]?.wins &&
                ` (${seed.teams[0].wins}-${seed.teams[0].losses})`}
            </SeedTeam>
            <SeedTeam
              onClick={(e: React.MouseEvent) =>
                handleClick(e, seed.id as number, "team2")
              }
              style={
                seed.teams[1]?.isWinner
                  ? {
                      fontWeight: "bold",
                      color: theme.palette.secondary.light,
                      borderLeft: `3px solid ${theme.palette.secondary.main}`,
                    }
                  : { borderLeft: "3px solid transparent" }
              }
            >
              {seed.teams[1].seed && `${seed.teams[1].seed} `}
              {seed.teams[1]?.name || " - "}
              {seed.teams[1]?.wins &&
                ` (${seed.teams[1].wins}-${seed.teams[1].losses})`}
            </SeedTeam>
          </div>
        </SeedItem>
      </Wrapper>
    );
  };

  if (loading) return <Loading />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Navbar />

      {/* Page header */}
      <Box
        sx={{
          background: theme.gradients.hero,
          color: "white",
          py: 4,
          textAlign: "center",
          px: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontFamily: theme.fonts.display, mb: 0.5 }}
        >
          Create Your Bracket
        </Typography>
        <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
          Click on a team to choose the winner of each game.
        </Typography>
      </Box>

      {/* Bracket area */}
      <Box sx={{ background: theme.gradients.contentBg, flex: 1 }}>
        {/* First Four */}
        <Paper elevation={1} sx={{ mx: { xs: 1, md: 3 }, mt: 3, p: 2 }}>
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight={600}
            gutterBottom
          >
            First Four
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ overflowX: "auto" }}>
            <Bracket
              mobileBreakpoint={0}
              rounds={firstFour}
              renderSeedComponent={CustomSeed}
              swipeableProps={{ enableMouseEvents: true, animateHeight: true }}
              roundTitleComponent={(title: React.ReactNode) => (
                <div
                  style={{ textAlign: "center", fontSize: "22px", color: theme.palette.primary.main, fontFamily: theme.fonts.display }}
                >
                  {title}
                </div>
              )}
            />
          </Box>
        </Paper>

        {/* Main bracket */}
        <Paper elevation={1} sx={{ mx: { xs: 1, md: 3 }, mt: 2, p: 2 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Bracket
              mobileBreakpoint={0}
              bracketClassName="custom-bracket"
              rounds={rounds}
              renderSeedComponent={CustomSeed}
              swipeableProps={{ enableMouseEvents: true, animateHeight: true }}
              twoSided={true}
              roundTitleComponent={(title: React.ReactNode) => (
                <div
                  style={{ textAlign: "center", fontSize: "22px", color: theme.palette.primary.main, fontFamily: theme.fonts.display }}
                >
                  {title}
                </div>
              )}
            />
          </Box>
        </Paper>

        {/* Submit row */}
        <Paper
          elevation={2}
          sx={{
            mx: { xs: 1, md: 3 },
            mt: 2,
            mb: 3,
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            inputRef={bracketNameRef}
            label="Bracket Name"
            placeholder="Enter a name for your bracket"
            variant="outlined"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
          >
            Submit Bracket
          </Button>
        </Paper>
      </Box>

      <Footer />
    </Box>
  );
};

export default BracketMaker;
